import axios from 'axios';
import { SITE_LOGIN } from '../config/siteLinks';

const TOKEN_KEY = 'maximuz_token';
// Mirrors AuthContext.js's IMPERSONATION_KEY — an impersonation session lives in
// this tab's sessionStorage only, so it never leaks into (or gets overwritten
// by) any other open tab's localStorage-backed session.
const IMPERSONATION_KEY = 'maximuz_impersonation_token';
const BASE_URL = '/api';

const getStoredToken = () => sessionStorage.getItem(IMPERSONATION_KEY) || localStorage.getItem(TOKEN_KEY);

const apiClient = axios.create({
  baseURL: BASE_URL,
  headers: { 'Content-Type': 'application/json' }
});

apiClient.interceptors.request.use(
  (config) => {
    const token = getStoredToken();
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  },
  (error) => Promise.reject(error)
);

apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    // A 401 means "your session expired" ONLY when we had a token AND the call
    // was not an authentication attempt. Sign-in, password-reset and OTP calls
    // legitimately return 401 for bad credentials; hijacking those tore the
    // login page down before it could show "Invalid credentials" and dumped
    // the user on a marketing page instead. That is a credential error the
    // page itself must be allowed to display.
    const url = error.config?.url || '';
    const isAuthAttempt = /\/auth\/(login|register|forgot-password|reset-password)/.test(url);
    if (error.response?.status === 401 && getStoredToken() && !isAuthAttempt) {
      sessionStorage.removeItem(IMPERSONATION_KEY);
      localStorage.removeItem(TOKEN_KEY);
      // Full page load, not a router push: /login is served by IIS as the
      // static role chooser and is not a React route.
      window.location.href = SITE_LOGIN;
    }
    return Promise.reject(error);
  }
);

// ============ AUTH API ============
export const authAPI = {
  register: (data) => apiClient.post('/auth/register', data),
  login: (data) => apiClient.post('/auth/login', data),
  forgotPassword: (data) => apiClient.post('/auth/forgot-password', data),
  resetPassword: (data) => apiClient.post('/auth/reset-password', data),
  getMe: () => apiClient.get('/auth/me'),
};

// ============ ADMIN API ============
export const adminAPI = {
  getDashboard: () => apiClient.get('/admin/dashboard'),
  getPerformanceOverview: () => apiClient.get('/admin/performance-overview'),
  getStudentPerformance: (studentId) => apiClient.get(`/admin/students/${studentId}/performance`),
  getBilling: () => apiClient.get('/admin/billing'),
  payBilling: (data) => apiClient.post('/admin/billing/payments', data),
  addCartItem: (data) => apiClient.post('/admin/billing/cart/items', data),
  removeCartItem: (tier) => apiClient.delete(`/admin/billing/cart/items/${tier}`),
  cancelCart: () => apiClient.delete('/admin/billing/cart'),
  requestCartDiscount: () => apiClient.post('/admin/billing/cart/request-discount'),
  createCheckoutOrder: () => apiClient.post('/admin/billing/cart/checkout'),
  verifyCartPayment: (data) => apiClient.post('/admin/billing/cart/verify-payment', data),
  getProfile: () => apiClient.get('/admin/profile'),
  updateProfile: (data) => apiClient.put('/admin/profile', data),
  changePassword: (data) => apiClient.put('/admin/change-password', data),

  getSchools: (params) => apiClient.get('/admin/schools', { params }),
  createSchool: (data) => apiClient.post('/admin/schools', data),
  updateSchool: (id, data) => apiClient.put(`/admin/schools/${id}`, data),
  deleteSchool: (id) => apiClient.delete(`/admin/schools/${id}`),

  getTeachers: (params) => apiClient.get('/admin/teachers', { params }),
  createTeacher: (data) => apiClient.post('/admin/teachers', data),
  updateTeacher: (id, data) => apiClient.put(`/admin/teachers/${id}`, data),
  deleteTeacher: (id) => apiClient.delete(`/admin/teachers/${id}`),
  assignTeacherBatch: (id, data) => apiClient.post(`/admin/teachers/${id}/assign-batch`, data),
  changeTeacherPassword: (id, data) => apiClient.put(`/admin/teachers/${id}/password`, data),
  importTeachers: (formData) => apiClient.post('/admin/teachers/import', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  downloadTeacherImportTemplate: (format) => apiClient.get('/admin/teachers/import-template', { params: format ? { format } : undefined, responseType: 'blob' }),

  getStudents: (params) => apiClient.get('/admin/students', { params }),
  createStudent: (data) => apiClient.post('/admin/students', data),
  updateStudent: (id, data) => apiClient.put(`/admin/students/${id}`, data),
  deleteStudent: (id) => apiClient.delete(`/admin/students/${id}`),
  importStudents: (formData) => apiClient.post('/admin/students/import', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  downloadStudentImportTemplate: (format) => apiClient.get('/admin/students/import-template', { params: format ? { format } : undefined, responseType: 'blob' }),
  assignStudentBatch: (id, data) => apiClient.post(`/admin/students/${id}/assign-batch`, data),
  changeStudentPassword: (id, data) => apiClient.put(`/admin/students/${id}/password`, data),

  getBatches: (params) => apiClient.get('/admin/batches', { params }),
  createBatch: (data) => apiClient.post('/admin/batches', data),
  updateBatch: (id, data) => apiClient.put(`/admin/batches/${id}`, data),
  deleteBatch: (id) => apiClient.delete(`/admin/batches/${id}`),
  getBatchStudents: (id) => apiClient.get(`/admin/batches/${id}/students`),
  addStudentToBatch: (batchId, data) => apiClient.post(`/admin/batches/${batchId}/students`, data),
  removeStudentFromBatch: (batchId, studentId) => apiClient.delete(`/admin/batches/${batchId}/students/${studentId}`),

  // params: { teacher_id?, from?, to? } — from/to (YYYY-MM-DD) limit one-off periods to a date range.
  getSchedules: (params) => apiClient.get('/admin/schedules', { params: params || {} }),
  createSchedule: (data) => apiClient.post('/admin/schedules', data),
  updateSchedule: (id, data) => apiClient.put(`/admin/schedules/${id}`, data),
  deleteSchedule: (id) => apiClient.delete(`/admin/schedules/${id}`),

  getAuditLog: (params) => apiClient.get('/admin/audit-log', { params }),
  getAuditLogActors: () => apiClient.get('/admin/audit-log/actors'),

  getAttendanceSettings: () => apiClient.get('/admin/attendance/settings'),
  updateAttendanceSettings: (data) => apiClient.put('/admin/attendance/settings', data),
  getAttendanceLog: (params) => apiClient.get('/admin/attendance/log', { params }),
  getAttendanceSummary: (params) => apiClient.get('/admin/attendance/summary', { params }),
  searchLocation: (q) => apiClient.get('/admin/attendance/geocode', { params: { q } }),

  getAccountants: (params) => apiClient.get('/admin/accountants', { params }),
  createAccountant: (data) => apiClient.post('/admin/accountants', data),
  updateAccountant: (id, data) => apiClient.put(`/admin/accountants/${id}`, data),
  deleteAccountant: (id) => apiClient.delete(`/admin/accountants/${id}`),
  changeAccountantPassword: (id, data) => apiClient.put(`/admin/accountants/${id}/password`, data),
};

// ============ FINANCE API (accountant + admin) ============
export const financeAPI = {
  getDashboard: () => apiClient.get('/finance/dashboard'),

  getAcademicYears: () => apiClient.get('/finance/academic-years'),
  createAcademicYear: (data) => apiClient.post('/finance/academic-years', data),
  updateAcademicYear: (id, data) => apiClient.put(`/finance/academic-years/${id}`, data),

  getFeeCategories: () => apiClient.get('/finance/categories'),
  createFeeCategory: (data) => apiClient.post('/finance/categories', data),
  deleteFeeCategory: (id) => apiClient.delete(`/finance/categories/${id}`),

  getFeeStructures: (academic_year_id) => apiClient.get('/finance/structures', { params: { academic_year_id } }),
  saveFeeStructure: (data) => apiClient.post('/finance/structures', data),
  deleteFeeStructure: (id) => apiClient.delete(`/finance/structures/${id}`),
  assignStructureToClass: (data) => apiClient.post('/finance/structures/assign-class', data),

  getStudentsFeeSummary: (params) => apiClient.get('/finance/students', { params }),
  getStudentLedger: (studentId, params) => apiClient.get(`/finance/students/${studentId}/ledger`, { params }),
  addStudentFee: (data) => apiClient.post('/finance/student-fees', data),
  updateStudentFee: (id, data) => apiClient.put(`/finance/student-fees/${id}`, data),
  deleteStudentFee: (id) => apiClient.delete(`/finance/student-fees/${id}`),

  recordPayment: (data) => apiClient.post('/finance/payments', data),
  // Always an authenticated blob fetch (the route needs a Bearer token, so a
  // plain <a href>/window.open to the raw URL would just 401) — the caller
  // turns the blob into an object URL, then either triggers <a download> or
  // window.open()s it for a print-style view. Reprinting an old receipt is
  // the exact same call, just invoked again from a payment history row.
  downloadReceipt: (paymentId) => apiClient.get(`/finance/payments/${paymentId}/receipt`, { responseType: 'blob' }),

  // "Pay everything at once" — one amount gets allocated across the
  // student's outstanding fee lines server-side, recorded as one batch, one
  // combined receipt.
  recordBulkPayment: (data) => apiClient.post('/finance/payments/bulk', data),
  downloadBatchReceipt: (batchId) => apiClient.get(`/finance/payment-batches/${batchId}/receipt`, { responseType: 'blob' }),
  searchReceipt: (receipt_number) => apiClient.get('/finance/receipts/search', { params: { receipt_number } }),

  getCollectionsReport: (params) => apiClient.get('/finance/reports/collections', { params }),
  // Bulk export of a collections search — csv/excel/pdf — always an
  // authenticated blob fetch (route needs a Bearer token, so a plain
  // <a href> to the raw URL would 401).
  exportCollectionsReport: (params) => apiClient.get('/finance/reports/collections/export', { params, responseType: 'blob' }),
  getPendingDues: (params) => apiClient.get('/finance/reports/pending-dues', { params }),

  // Corrections — an accountant can only file a request; resolving one or
  // voiding a receipt directly is admin-only server-side (see routes/finance.js).
  createVoidRequest: (data) => apiClient.post('/finance/void-requests', data),
  getVoidRequests: (params) => apiClient.get('/finance/void-requests', { params }),
  resolveVoidRequest: (id, data) => apiClient.post(`/finance/void-requests/${id}/resolve`, data),
  voidPaymentDirect: (data) => apiClient.post('/finance/payments/void', data),

  changePassword: (data) => apiClient.put('/finance/change-password', data),
};

// ============ TEACHER API ============
export const teacherAPI = {
  getDashboard: () => apiClient.get('/teacher/dashboard'),
  getProfile: () => apiClient.get('/teacher/profile'),
  updateProfile: (data) => apiClient.put('/teacher/profile', data),
  changePassword: (data) => apiClient.put('/teacher/change-password', data),
  getBatches: () => apiClient.get('/teacher/batches'),
  getBatchStudents: (id) => apiClient.get(`/teacher/batches/${id}/students`),
  getTests: () => apiClient.get('/teacher/tests'),
  getTestReport: (id) => apiClient.get(`/teacher/tests/${id}/report`),
  getClassReport: () => apiClient.get('/teacher/class-report'),
  getChapterAnalysis: (testId) => apiClient.get(`/teacher/tests/${testId}/chapter-analysis`),
  getTopicAnalysis: (testId) => apiClient.get(`/teacher/tests/${testId}/topic-analysis`),
  getDifficultyAnalysis: (testId) => apiClient.get(`/teacher/tests/${testId}/difficulty-analysis`),
  getStudentReport: (id) => apiClient.get(`/teacher/students/${id}/report`),
  getAtRiskStudents: () => apiClient.get('/teacher/at-risk-students'),
  getTopPerformers: () => apiClient.get('/teacher/top-performers'),
  getStudentPerformance: (studentId) => apiClient.get(`/teacher/students/${studentId}/performance`),
  getSchedule: () => apiClient.get('/teacher/schedule'),
  getAnalyticsSummary: () => apiClient.get('/teacher/analytics/summary'),
  getSubjectPerformance: () => apiClient.get('/teacher/analytics/subject-performance'),
  getAnalyticsChapterPerformance: () => apiClient.get('/teacher/analytics/chapter-performance'),
  getAnalyticsTopicPerformance: () => apiClient.get('/teacher/analytics/topic-performance'),
  getImprovementTrend: () => apiClient.get('/teacher/analytics/improvement-trend'),
  getPendingEvaluations: () => apiClient.get('/teacher/pending-evaluations'),
  getTodayAttendance: () => apiClient.get('/teacher/attendance/today'),
  getAttendanceHistory: (params) => apiClient.get('/teacher/attendance/history', { params }),
  punchAttendance: (data) => apiClient.post('/teacher/attendance/punch', data),
};

// ============ INSTRUCTOR API ============
// An instructor is a teacher-equivalent scoped to their own org-of-one (see
// instructorController.js) — test/question-paper creation reuses testAPI/
// questionAPI directly, and viewing their students' performance reuses
// teacherAPI directly too (routes/teacher.js authorizes 'instructor').
// Student roster CRUD is Super Admin-only now — see superadminAPI's
// getOrgStudents/createOrgStudent/permanentlyDeleteStudent.
export const instructorAPI = {
  getDashboard: () => apiClient.get('/instructor/dashboard'),
  getProfile: () => apiClient.get('/instructor/profile'),
  changePassword: (data) => apiClient.put('/instructor/change-password', data),
  // Individual students mapped to this instructor, plus everyone enrolled on
  // a course assigned to them.
  getMyStudents: () => apiClient.get('/instructor/students'),
  // Courses the Super Admin assigned to this instructor — also the "course
  // groups" a test can be assigned to.
  getMyCourses: () => apiClient.get('/instructor/courses'),
  getMyCourse: (id) => apiClient.get(`/instructor/courses/${id}`),
};

// ============ STUDENT API ============
export const studentAPI = {
  getDashboard: () => apiClient.get('/student/dashboard'),
  getProfile: () => apiClient.get('/student/profile'),
  updateProfile: (data) => apiClient.put('/student/profile', data),
  changePassword: (data) => apiClient.put('/student/change-password', data),
  getPerformance: () => apiClient.get('/student/performance'),
  getMySubjects: () => apiClient.get('/student/subjects'),
  getMyChapters: (params) => apiClient.get('/student/chapters', { params }),
  getMyTopics: (params) => apiClient.get('/student/topics', { params }),
};

// ============ ADMISSIONS API ============
// The public marketing funnel's lead capture, plus the superadmin pipeline.
// submitEnquiry/getClasses are deliberately unauthenticated — they're called
// from the landing page by visitors who have no account yet.
export const admissionAPI = {
  submitEnquiry: (data) => apiClient.post('/admissions', data),
  getClasses: () => apiClient.get('/admissions/classes'),
  getEnquiries: (params) => apiClient.get('/admissions', { params }),
  updateStatus: (id, status) => apiClient.patch(`/admissions/${id}`, { status }),
  exportEnquiries: (params) => apiClient.get('/admissions/export', { params, responseType: 'blob' }),
};

// ============ MINTT 2026 REGISTRATIONS ============
export const minttAPI = {
  getRegistrations: (params) => apiClient.get('/mintt/registrations', { params }),
  getRegistration: (id) => apiClient.get(`/mintt/registrations/${id}`),
  updateRegistration: (id, data) => apiClient.patch(`/mintt/registrations/${id}`, data),
  exportRegistrations: (params) => apiClient.get('/mintt/registrations/export', { params, responseType: 'blob' }),
};

// ============ ARCHIVE API ============
// Soft-deleted curriculum + questions, and the path to bring them back.
export const archiveAPI = {
  getSummary: () => apiClient.get('/archive/summary'),
  getRecords: (params) => apiClient.get('/archive/records', { params }),
  getRecordIds: (params) => apiClient.get('/archive/records/ids', { params }),
  previewRestore: (data) => apiClient.post('/archive/restore/preview', data),
  restore: (data) => apiClient.post('/archive/restore', data),
};

// ============ QUESTION API ============
export const questionAPI = {
  getClasses: () => apiClient.get('/questions/classes'),
  createClass: (data) => apiClient.post('/questions/classes', data),
  deleteClass: (id) => apiClient.delete(`/questions/classes/${id}`),

  getSubjects: (params) => apiClient.get('/questions/subjects', { params }),
  createSubject: (data) => apiClient.post('/questions/subjects', data),
  updateSubject: (id, data) => apiClient.put(`/questions/subjects/${id}`, data),
  deleteSubject: (id) => apiClient.delete(`/questions/subjects/${id}`),

  getChapters: (params) => apiClient.get('/questions/chapters', { params }),
  createChapter: (data) => apiClient.post('/questions/chapters', data),
  updateChapter: (id, data) => apiClient.put(`/questions/chapters/${id}`, data),
  deleteChapter: (id) => apiClient.delete(`/questions/chapters/${id}`),

  getTopics: (params) => apiClient.get('/questions/topics', { params }),
  searchCurriculum: (params) => apiClient.get('/questions/curriculum/search', { params }),
  checkDuplicate: (params) => apiClient.get('/questions/duplicate-check', { params }),
  createTopic: (data) => apiClient.post('/questions/topics', data),
  updateTopic: (id, data) => apiClient.put(`/questions/topics/${id}`, data),
  deleteTopic: (id) => apiClient.delete(`/questions/topics/${id}`),

  getQuestions: (params) => apiClient.get('/questions', { params }),
  getQuestionIds: (params) => apiClient.get('/questions/ids', { params }),
  getQuestionById: (id) => apiClient.get(`/questions/${id}`),
  createQuestion: (data) => apiClient.post('/questions', data),
  updateQuestion: (id, data) => apiClient.put(`/questions/${id}`, data),
  deleteQuestion: (id) => apiClient.delete(`/questions/${id}`),
  bulkDeleteQuestions: (ids) => apiClient.post('/questions/bulk-delete', { ids }),
  getPendingReviewQuestions: (params) => apiClient.get('/questions/pending-review', { params }),
  acceptPendingQuestion: (id) => apiClient.post(`/questions/${id}/accept`),
  dismissPendingQuestion: (id) => apiClient.post(`/questions/${id}/dismiss-review`),
  importFromExcel: (formData, bank) => apiClient.post('/questions/import/excel', formData, { headers: { 'Content-Type': 'multipart/form-data' }, params: bank ? { bank } : undefined }),
  importFromZip: (formData, bank) => apiClient.post('/questions/import/zip', formData, { headers: { 'Content-Type': 'multipart/form-data' }, params: bank ? { bank } : undefined }),
  confirmImport: (token, duplicate_action) => apiClient.post('/questions/import/confirm', { token, duplicate_action }),
  previewImport: (formData, bank, isZip) => apiClient.post(`/questions/import/preview/${isZip ? 'zip' : 'excel'}`, formData, { headers: { 'Content-Type': 'multipart/form-data' }, params: { bank } }),
  getMyUploadSummary: () => apiClient.get('/questions/my-uploads/summary'),
  uploadImage: (formData) => apiClient.post('/questions/upload-image', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  downloadTemplate: (format) => apiClient.get('/questions/template', { params: format ? { format } : undefined, responseType: 'blob' }),
  importCurriculum: (formData) => apiClient.post('/questions/curriculum/import', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  downloadCurriculumTemplate: (format) => apiClient.get('/questions/curriculum/template', { params: format ? { format } : undefined, responseType: 'blob' }),
};

// ============ TEST API ============
export const testAPI = {
  getAllowedClasses: () => apiClient.get('/tests/allowed-classes'),
  getTests: (params) => apiClient.get('/tests', { params }),
  getTestById: (id) => apiClient.get(`/tests/${id}`),
  previewTest: (id) => apiClient.get(`/tests/${id}/preview`),
  setQuestionVisibility: (id, questionId, hidden) => apiClient.patch(`/tests/${id}/questions/${questionId}/visibility`, { hidden }),
  createTest: (data) => apiClient.post('/tests', data),
  updateTest: (id, data) => apiClient.put(`/tests/${id}`, data),
  deleteTest: (id) => apiClient.delete(`/tests/${id}`),
  activateTest: (id) => apiClient.post(`/tests/${id}/activate`),
  deactivateTest: (id) => apiClient.post(`/tests/${id}/deactivate`),
  assignTestToBatch: (id, data) => apiClient.post(`/tests/${id}/assign-batch`, data),
  startAttempt: (testId) => apiClient.post(`/tests/${testId}/start-attempt`),
  saveAnswer: (attemptId, data) => apiClient.post(`/tests/attempts/${attemptId}/save-answer`, data),
  submitAttempt: (attemptId) => apiClient.post(`/tests/attempts/${attemptId}/submit`),
  getAttemptResult: (attemptId) => apiClient.get(`/tests/attempts/${attemptId}/result`),
  getStudentAttempts: () => apiClient.get('/tests/my-attempts'),
  getAvailableTests: () => apiClient.get('/tests/available'),
  getDailyQuiz: () => apiClient.get('/tests/daily-quiz'),
  bulkUploadTests: (formData) => apiClient.post('/tests/bulk-upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  downloadTestUploadTemplate: (format) => apiClient.get('/tests/bulk-upload/template', { params: format ? { format } : undefined, responseType: 'blob' }),
  bulkUploadQuestionsAndTests: (formData, bank) => apiClient.post('/tests/bulk-upload-combined', formData, { headers: { 'Content-Type': 'multipart/form-data' }, params: bank ? { bank } : undefined }),
  downloadCombinedUploadTemplate: (format) => apiClient.get('/tests/bulk-upload-combined/template', { params: format ? { format } : undefined, responseType: 'blob' }),
};

// ============ SUPERADMIN API ============
export const superadminAPI = {
  getDashboard: () => apiClient.get('/superadmin/dashboard'),
  getCurriculumAnalytics: () => apiClient.get('/superadmin/curriculum-analytics'),
  getQuestionBankOverride: () => apiClient.get('/superadmin/settings/question-bank-override'),
  setQuestionBankOverride: (value) => apiClient.put('/superadmin/settings/question-bank-override', { value }),
  getAuditLog: (params) => apiClient.get('/superadmin/audit-log', { params }),
  getAuditLogActors: () => apiClient.get('/superadmin/audit-log/actors'),
  restoreQuestionBankOverride: () => apiClient.post('/superadmin/settings/question-bank-override/restore'),
  getPlatformPerformanceOverview: () => apiClient.get('/superadmin/performance-overview'),
  getOrgPerformanceOverview: (orgId) => apiClient.get(`/superadmin/customers/${orgId}/performance-overview`),
  getOrgStudentPerformance: (orgId, studentId) => apiClient.get(`/superadmin/customers/${orgId}/students/${studentId}/performance`),
  getInstructors: (params) => apiClient.get('/superadmin/instructors', { params }),
  // Delete = archive when the instructor has history; the check says which.
  instructorDeleteCheck: (orgId) => apiClient.get(`/superadmin/instructors/${orgId}/delete-check`),
  deleteInstructor: (orgId, data) => apiClient.delete(`/superadmin/instructors/${orgId}`, { data }),
  restoreInstructor: (orgId) => apiClient.post(`/superadmin/instructors/${orgId}/restore`),
  updateInstructor: (orgId, data) => apiClient.put(`/superadmin/instructors/${orgId}`, data),
  createInstructor: (data) => apiClient.post('/superadmin/instructors', data),
  toggleInstructor: (orgId) => apiClient.patch(`/superadmin/instructors/${orgId}/toggle`),
  resetInstructorPassword: (orgId, new_password) => apiClient.put(`/superadmin/instructors/${orgId}/password`, { new_password }),

  // Which individual students an instructor may reach. The id may be either the
  // instructor's org id or their user id — the backend resolves both, since the
  // Instructors screen has historically carried the org id.
  // Log in as an instructor, for testing and support.
  impersonateInstructor: (id) => apiClient.post(`/superadmin/instructors/${id}/impersonate`),
  getInstructorStudents: (id) => apiClient.get(`/superadmin/instructors/${id}/students`),
  getInstructorAvailableStudents: (id, params) => apiClient.get(`/superadmin/instructors/${id}/available-students`, { params }),
  assignInstructorStudents: (id, data) => apiClient.post(`/superadmin/instructors/${id}/students`, data),
  updateInstructorStudent: (id, studentId, data) => apiClient.put(`/superadmin/instructors/${id}/students/${studentId}`, data),
  unassignInstructorStudent: (id, studentId) => apiClient.delete(`/superadmin/instructors/${id}/students/${studentId}`),

  getCustomers: () => apiClient.get('/superadmin/customers'),
  createCustomer: (data) => apiClient.post('/superadmin/customers', data),
  getCustomerDetail: (id) => apiClient.get(`/superadmin/customers/${id}`),
  updateOrganization: (id, data) => apiClient.put(`/superadmin/customers/${id}`, data),
  toggleCustomer: (id) => apiClient.patch(`/superadmin/customers/${id}/toggle`),
  updatePlan: (id, data) => apiClient.put(`/superadmin/customers/${id}/plan`, data),
  getOrgAllowedClasses: (id) => apiClient.get(`/superadmin/customers/${id}/allowed-classes`),
  setOrgAllowedClasses: (id, classes) => apiClient.put(`/superadmin/customers/${id}/allowed-classes`, { classes }),
  approveCartDiscount: (id, data) => apiClient.post(`/superadmin/customers/${id}/plan/cart/approve-discount`, data),
  dismissCart: (id) => apiClient.post(`/superadmin/customers/${id}/plan/cart/dismiss`),
  getPayments: (id) => apiClient.get(`/superadmin/customers/${id}/payments`),
  recordPayment: (id, data) => apiClient.post(`/superadmin/customers/${id}/payments`, data),
  getBillingOverview: () => apiClient.get('/superadmin/billing'),
  updateAdmin: (orgId, adminId, data) => apiClient.put(`/superadmin/customers/${orgId}/admins/${adminId}`, data),
  resetAdminPassword: (orgId, adminId, data) => apiClient.put(`/superadmin/customers/${orgId}/admins/${adminId}/password`, data),
  impersonateAdmin: (orgId, adminId) => apiClient.post(`/superadmin/customers/${orgId}/admins/${adminId}/impersonate`),
  getProfile: () => apiClient.get('/superadmin/profile'),
  updateProfile: (data) => apiClient.put('/superadmin/profile', data),
  changePassword: (data) => apiClient.put('/superadmin/change-password', data),

  getOrgSuperadmins: (orgId) => apiClient.get(`/superadmin/customers/${orgId}/superadmins`),
  createOrgSuperadmin: (orgId, data) => apiClient.post(`/superadmin/customers/${orgId}/superadmins`, data),
  resetOrgSuperadminPassword: (orgId, superadminId, data) => apiClient.put(`/superadmin/customers/${orgId}/superadmins/${superadminId}/password`, data),
  toggleOrgSuperadmin: (orgId, superadminId) => apiClient.patch(`/superadmin/customers/${orgId}/superadmins/${superadminId}/toggle`),

  getOrgTests: (orgId, params) => apiClient.get(`/superadmin/customers/${orgId}/tests`, { params }),
  permanentlyDeleteTest: (orgId, testId) => apiClient.delete(`/superadmin/customers/${orgId}/tests/${testId}`),

  getOrgStudents: (orgId, params) => apiClient.get(`/superadmin/customers/${orgId}/students`, { params }),
  createOrgStudent: (orgId, data) => apiClient.post(`/superadmin/customers/${orgId}/students`, data),
  updateOrgStudent: (orgId, studentId, data) => apiClient.put(`/superadmin/customers/${orgId}/students/${studentId}`, data),
  permanentlyDeleteStudent: (orgId, studentId) => apiClient.delete(`/superadmin/customers/${orgId}/students/${studentId}`),
  impersonateStudent: (orgId, studentId) => apiClient.post(`/superadmin/customers/${orgId}/students/${studentId}/impersonate`),

  getOrgTeachers: (orgId, params) => apiClient.get(`/superadmin/customers/${orgId}/teachers`, { params }),
  createOrgTeacher: (orgId, data) => apiClient.post(`/superadmin/customers/${orgId}/teachers`, data),
  permanentlyDeleteTeacher: (orgId, teacherId) => apiClient.delete(`/superadmin/customers/${orgId}/teachers/${teacherId}`),
  impersonateTeacher: (orgId, teacherId) => apiClient.post(`/superadmin/customers/${orgId}/teachers/${teacherId}/impersonate`),

  getOrgAccountants: (orgId, params) => apiClient.get(`/superadmin/customers/${orgId}/accountants`, { params }),
  createOrgAccountant: (orgId, data) => apiClient.post(`/superadmin/customers/${orgId}/accountants`, data),
  permanentlyDeleteAccountant: (orgId, accountantId) => apiClient.delete(`/superadmin/customers/${orgId}/accountants/${accountantId}`),
  impersonateAccountant: (orgId, accountantId) => apiClient.post(`/superadmin/customers/${orgId}/accountants/${accountantId}/impersonate`),

  getOrgFinanceSummary: (orgId) => apiClient.get(`/superadmin/customers/${orgId}/finance-summary`),

  getOrgSchools: (orgId) => apiClient.get(`/superadmin/customers/${orgId}/schools`),
  getOrgBatches: (orgId) => apiClient.get(`/superadmin/customers/${orgId}/batches`),

  // Official (unscoped) superadmin only — server-side enforced too.
  lookupEmail: (email) => apiClient.get('/superadmin/email-lookup', { params: { email } }),

  getWhatsAppTemplates: () => apiClient.get('/superadmin/whatsapp/templates'),
  testSendWhatsApp: (data) => apiClient.post('/superadmin/whatsapp/test-send', data),
  getWhatsAppDashboard: (params) => apiClient.get('/superadmin/whatsapp/dashboard', { params }),
  requestWhatsAppVerificationCode: (code_method) => apiClient.post('/superadmin/whatsapp/request-verification-code', { code_method }),
  confirmWhatsAppVerificationCode: (code) => apiClient.post('/superadmin/whatsapp/verify-code', { code }),

  getSupportTickets: (params) => apiClient.get('/superadmin/support/tickets', { params }),
  getSupportTicket: (id) => apiClient.get(`/superadmin/support/tickets/${id}`),
  replySupportTicket: (id, message) => apiClient.post(`/superadmin/support/tickets/${id}/reply`, { message }),
  updateSupportTicketStatus: (id, status) => apiClient.patch(`/superadmin/support/tickets/${id}/status`, { status }),
  getSupportUnreadCount: () => apiClient.get('/superadmin/support/tickets/unread-count'),

  getLocalWhatsAppTemplates: () => apiClient.get('/superadmin/whatsapp/templates/local'),
  syncWhatsAppTemplates: () => apiClient.post('/superadmin/whatsapp/templates/sync'),
  createWhatsAppTemplate: (data) => apiClient.post('/superadmin/whatsapp/templates/create', data),
  deleteWhatsAppTemplate: (name, organizationId) => apiClient.delete(`/superadmin/whatsapp/templates/${encodeURIComponent(name)}`, { params: organizationId ? { organization_id: organizationId } : {} }),
  getOrgTemplateMappings: () => apiClient.get('/superadmin/whatsapp/org-templates'),
  setOrgTemplateMapping: (orgId, data) => apiClient.put(`/superadmin/whatsapp/org-templates/${orgId}`, data),
  deleteOrgTemplateMapping: (orgId) => apiClient.delete(`/superadmin/whatsapp/org-templates/${orgId}`),
  getWorkflowTemplateMappings: () => apiClient.get('/superadmin/whatsapp/workflow-templates'),
  setWorkflowTemplateMapping: (key, data) => apiClient.put(`/superadmin/whatsapp/workflow-templates/${key}`, data),
  deleteWorkflowTemplateMapping: (key) => apiClient.delete(`/superadmin/whatsapp/workflow-templates/${key}`),

  getWhatsAppAccounts: () => apiClient.get('/superadmin/whatsapp/accounts'),
  upsertWhatsAppAccount: (orgId, data) => apiClient.put(`/superadmin/whatsapp/accounts/${orgId}`, data),
  deleteWhatsAppAccount: (orgId) => apiClient.delete(`/superadmin/whatsapp/accounts/${orgId}`),
  refreshWhatsAppAccountStatus: (orgId) => apiClient.post(`/superadmin/whatsapp/accounts/${orgId}/refresh-status`),
  requestAccountVerificationCode: (orgId, code_method) => apiClient.post(`/superadmin/whatsapp/accounts/${orgId}/request-verification-code`, { code_method }),
  confirmAccountVerificationCode: (orgId, code) => apiClient.post(`/superadmin/whatsapp/accounts/${orgId}/verify-code`, { code }),
  syncAccountTemplates: (orgId) => apiClient.post(`/superadmin/whatsapp/accounts/${orgId}/templates/sync`),

  // WhatsApp Numbers — the central pool (add/verify/edit/delete/assign),
  // replacing the old per-org provisionWhatsAppNumber quick-add above.
  getWhatsAppNumbers: () => apiClient.get('/superadmin/whatsapp/numbers'),
  addWhatsAppNumber: (data) => apiClient.post('/superadmin/whatsapp/numbers', data),
  sendWhatsAppNumberCode: (id, code_method) => apiClient.post(`/superadmin/whatsapp/numbers/${id}/send-code`, { code_method }),
  verifyWhatsAppNumberCode: (id, code) => apiClient.post(`/superadmin/whatsapp/numbers/${id}/verify-code`, { code }),
  updateWhatsAppNumberName: (id, verified_name) => apiClient.put(`/superadmin/whatsapp/numbers/${id}/name`, { verified_name }),
  deleteWhatsAppNumber: (id) => apiClient.delete(`/superadmin/whatsapp/numbers/${id}`),
  toggleWhatsAppNumber: (id) => apiClient.patch(`/superadmin/whatsapp/numbers/${id}/toggle`),
  refreshWhatsAppNumber: (id) => apiClient.post(`/superadmin/whatsapp/numbers/${id}/refresh`),
  assignWhatsAppNumber: (id, organization_ids) => apiClient.post(`/superadmin/whatsapp/numbers/${id}/assign`, { organization_ids }),
  unassignWhatsAppNumber: (id, orgId) => apiClient.delete(`/superadmin/whatsapp/numbers/${id}/assign/${orgId}`),

  // WhatsApp Reports — org+number-grouped activity analytics with filters
  // and CSV/Excel/PDF export. getWhatsAppAnalyticsSummary is also what the
  // Super Admin Dashboard's WhatsApp Message Activity card calls.
  getWhatsAppAnalyticsSummary: (params) => apiClient.get('/superadmin/whatsapp/analytics/summary', { params }),
  getWhatsAppReports: (params) => apiClient.get('/superadmin/whatsapp/reports', { params }),
  getWhatsAppReportFilters: () => apiClient.get('/superadmin/whatsapp/reports/filters'),
  exportWhatsAppReports: (params) => apiClient.get('/superadmin/whatsapp/reports/export', { params, responseType: 'blob' }),
  getWhatsAppMessageLog: (params) => apiClient.get('/superadmin/whatsapp/message-log', { params }),
  exportWhatsAppMessageLog: (params) => apiClient.get('/superadmin/whatsapp/message-log/export', { params, responseType: 'blob' }),
  retryWhatsAppMessage: (id) => apiClient.post(`/superadmin/whatsapp/message-log/${id}/retry`),

  getMenuRegistry: () => apiClient.get('/superadmin/permissions/menu-registry'),
  setRolePermissions: (role, permissions) => apiClient.put('/superadmin/permissions', { role, permissions }),

  // Multi-org "Users tab" sub-accounts (see superadminUsersController.js)
  getSuperadminUsers: () => apiClient.get('/superadmin/users'),
  createSuperadminUser: (data) => apiClient.post('/superadmin/users', data),
  updateSuperadminUserOrgs: (userId, organization_ids) => apiClient.put(`/superadmin/users/${userId}/orgs`, { organization_ids }),
  resetSuperadminUserPassword: (userId, new_password) => apiClient.put(`/superadmin/users/${userId}/password`, { new_password }),
  toggleSuperadminUser: (userId) => apiClient.patch(`/superadmin/users/${userId}/toggle`),
  getSuperadminUserMenuRegistry: (userId) => apiClient.get(`/superadmin/users/${userId}/menu-registry`),
  setSuperadminUserPermissions: (userId, permissions) => apiClient.put(`/superadmin/users/${userId}/permissions`, { permissions }),
  getMySuperadminMenu: () => apiClient.get('/superadmin/my-menu'),
};

// ============ INDIVIDUAL STUDENTS (direct-enrolment flow) ============
// Separate from the school flow: these students belong to no organization and
// pay once via a Razorpay payment link instead of a school subscription.
export const individualAPI = {
  // Student's own demo/validity state — polled by the header countdown.
  getMyAccess: () => apiClient.get('/individual/my-access'),

  getCourses: (params) => apiClient.get('/individual/courses', { params }),
  // Students on one course, each with their class.
  getCourseStudents: (id) => apiClient.get(`/individual/courses/${id}/students`),
  createCourse: (data) => apiClient.post('/individual/courses', data),
  updateCourse: (id, data) => apiClient.put(`/individual/courses/${id}`, data),
  // Delete = archive when the course has history, blocked while students are
  // enrolled; courseDeleteCheck says which before anything happens.
  courseDeleteCheck: (id) => apiClient.get(`/individual/courses/${id}/delete-check`),
  deleteCourse: (id) => apiClient.delete(`/individual/courses/${id}`),
  setCourseStatus: (id, is_active) => apiClient.patch(`/individual/courses/${id}/status`, { is_active }),
  restoreCourse: (id) => apiClient.post(`/individual/courses/${id}/restore`),

  getStudents: (params) => apiClient.get('/individual/students', { params }),
  createStudent: (data) => apiClient.post('/individual/students', data),
  updateStudent: (id, data) => apiClient.put(`/individual/students/${id}`, data),
  studentDeleteCheck: (id) => apiClient.get(`/individual/students/${id}/delete-check`),
  deleteStudent: (id) => apiClient.delete(`/individual/students/${id}`),
  restoreStudent: (id) => apiClient.post(`/individual/students/${id}/restore`),
  // { course_id, class?, charge?, discount_percent? }
  changeStudentCourse: (id, data) => apiClient.post(`/individual/students/${id}/change-course`, data),
  extendDemo: (id, days) => apiClient.post(`/individual/students/${id}/extend-demo`, { days }),
  // Log in as an individual student. A separate route from the customer one
  // because that scopes by organization, and these students belong to none.
  impersonate: (studentId) => apiClient.post(`/individual/students/${studentId}/impersonate`),
  // Replaces the password and returns the new one once. There is no endpoint
  // that reveals the existing one — it is stored only as a bcrypt hash.
  resetPassword: (id, newPassword) =>
    apiClient.post(`/individual/students/${id}/reset-password`, { new_password: newPassword || undefined }),

  getPayments: (params) => apiClient.get('/individual/payments', { params }),
  getWebhookEvents: () => apiClient.get('/individual/webhook-events'),
  // Every Razorpay transaction across both flows, and the pull that recovers
  // payments no webhook was ever received for.
  getTransactions: (params) => apiClient.get('/individual/transactions', { params }),
  syncTransactions: (days) => apiClient.post('/individual/transactions/sync', { days }),

  regeneratePaymentLink: (enrollmentId) => apiClient.post(`/individual/enrollments/${enrollmentId}/payment-link`),
  downloadInvoice: (enrollmentId) =>
    apiClient.get(`/individual/enrollments/${enrollmentId}/invoice`, { responseType: 'blob' }),
};

// ============ PERMISSIONS (any authenticated role) API ============
export const permissionsAPI = {
  getMyMenu: () => apiClient.get('/permissions/my-menu'),
  // Per-user sidebar overrides, edited from a student's profile.
  getUserMenu: (userId) => apiClient.get(`/permissions/users/${userId}/menu`),
  setUserMenu: (userId, permissions) => apiClient.put(`/permissions/users/${userId}/menu`, { permissions }),
};

// ============ REPORT API ============
export const reportAPI = {
  getAnalytics: () => apiClient.get('/reports/analytics'),
  getStudentReport: (id) => apiClient.get(`/reports/student/${id}`),
  getBatchReport: (id) => apiClient.get(`/reports/batch/${id}`),
  getExamReport: (id) => apiClient.get(`/reports/exam/${id}`),
  downloadPDF: (type, id) => apiClient.get(`/reports/download/pdf/${type}/${id}`, { responseType: 'blob' }),
  downloadExcel: (type, id) => apiClient.get(`/reports/download/excel/${type}/${id}`, { responseType: 'blob' }),
  sendWhatsApp: (attemptId, phone) => apiClient.post(`/reports/attempts/${attemptId}/send-whatsapp`, phone ? { phone } : {}),
  sendWhatsAppBulk: (testId) => apiClient.post(`/reports/tests/${testId}/send-whatsapp-bulk`),
};

// ============ PUBLIC (no-login) REPORT LINK ============
// Opened from a WhatsApp message — scoped entirely by the opaque token, no
// auth required (and none sent, since a parent has no account).
export const publicAPI = {
  getSharedReport: (token) => apiClient.get(`/public/reports/${token}`),
  downloadSharedReportPDF: (token) => apiClient.get(`/public/reports/${token}/pdf`, { responseType: 'blob' }),
};

// ============ EBOOK API ============
// Method names are generic (getDocuments/createDocument/...) rather than
// Ebook-specific so this and questionPaperAPI below share the exact same
// shape — both get passed into the same DocumentLibrary components.
export const ebookAPI = {
  getDocuments: () => apiClient.get('/ebooks'),
  createDocument: (formData) => apiClient.post('/ebooks', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  updateDocument: (id, data) => apiClient.put(`/ebooks/${id}`, data),
  deleteDocument: (id) => apiClient.delete(`/ebooks/${id}`),
};

// ============ OLD QUESTION PAPERS API ============
export const questionPaperAPI = {
  getDocuments: () => apiClient.get('/question-papers'),
  createDocument: (formData) => apiClient.post('/question-papers', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  updateDocument: (id, data) => apiClient.put(`/question-papers/${id}`, data),
  deleteDocument: (id) => apiClient.delete(`/question-papers/${id}`),
};

// ============ CONTACT / INQUIRIES API ============
// submitInquiry is public (no login) — apiClient still works fine for it,
// the auth interceptor only attaches a token if one happens to be in
// storage, it never requires one.
export const contactAPI = {
  submitInquiry: (data) => apiClient.post('/contact', data),
  getInquiries: () => apiClient.get('/contact'),
  updateInquiryStatus: (id, status) => apiClient.patch(`/contact/${id}`, { status }),
};

// ============ SUPPORT (chatbot escalation) API ============
export const supportAPI = {
  requestHuman: (subject, message) => apiClient.post('/support/request-human', { subject, message }),
  getActiveTicket: () => apiClient.get('/support/tickets/active'),
  getTicket: (id) => apiClient.get(`/support/tickets/${id}`),
  replyToTicket: (id, message) => apiClient.post(`/support/tickets/${id}/reply`, { message }),
};

export default apiClient;
